import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Check, Loader2, Search, AlertCircle, Plus, UserPlus } from 'lucide-react';
import { useBusinessId } from '@/hooks/useBusinessId';
import { useBusinessCurrency } from '@/hooks/useBusinessCurrency';
import { formatPrice as formatCurrency } from '@/utils/currency';
import { catalogApi, type CatalogCategory, type CatalogService } from '@/api/catalog';
import { customersApi, type Customer } from '@/api/customers';
import { teamApi, type TimeSlot } from '@/api/team';
import { workHoursApi } from '@/api/workHours';
import { appointmentsApi } from '@/api/appointments';
import { userApi } from '@/api/user';
import { getApiErrorMessage } from '@/api/errors';
import { formatTime } from '@/utils/time';
import { toDateStr } from '@/utils/date';
import { CreateCustomerModal } from '../components/create-customer-modal';

// ── Helpers ────────────────────────────────────────────────────────────

const CUSTOMER_PAGE_SIZE = 8;

const DAY_NAMES_SHORT = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const MONTH_NAMES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

/** JS getDay() (0=Sun) → backend day string */
const JS_DAY_TO_BACKEND: Record<number, string> = {
  0: 'SUNDAY', 1: 'MONDAY', 2: 'TUESDAY', 3: 'WEDNESDAY',
  4: 'THURSDAY', 5: 'FRIDAY', 6: 'SATURDAY',
};

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function getInitials(firstName: string, lastName: string): string {
  return ((firstName[0] ?? '') + (lastName[0] ?? '')).toUpperCase() || '?';
}

// ── Mini Calendar ─────────────────────────────────────────────────────

function MiniCalendar({
  selectedDate,
  onSelectDate,
  disabledDays,
}: {
  selectedDate: string;
  onSelectDate: (dateStr: string) => void;
  disabledDays: Set<string>;
}) {
  const today = new Date();
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [viewYear, setViewYear] = useState(today.getFullYear());

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  // Can't go before current month
  const canGoPrev = viewYear > today.getFullYear() || (viewYear === today.getFullYear() && viewMonth > today.getMonth());

  // Build calendar grid
  const firstDayOfMonth = new Date(viewYear, viewMonth, 1);
  const startDay = firstDayOfMonth.getDay(); // 0=Sun
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

  const cells: (Date | null)[] = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(viewYear, viewMonth, d));

  const todayStr = toDateStr(today);

  return (
    <div>
      {/* Month header */}
      <div className="flex items-center justify-between mb-3">
        <button
          onClick={prevMonth}
          disabled={!canGoPrev}
          className="flex items-center justify-center rounded-lg transition-colors hover:bg-[#FAF9F7] disabled:opacity-30 disabled:cursor-not-allowed"
          style={{ width: '32px', height: '32px' }}
        >
          <ChevronLeft size={18} style={{ color: 'var(--dark-teal)' }} />
        </button>
        <span style={{ fontFamily: 'var(--font-heading)', fontSize: '15px', color: 'var(--dark-teal)', textTransform: 'capitalize' }}>
          {MONTH_NAMES[viewMonth]} {viewYear}
        </span>
        <button
          onClick={nextMonth}
          className="flex items-center justify-center rounded-lg transition-colors hover:bg-[#FAF9F7]"
          style={{ width: '32px', height: '32px' }}
        >
          <ChevronRight size={18} style={{ color: 'var(--dark-teal)' }} />
        </button>
      </div>

      {/* Day-of-week headers */}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {DAY_NAMES_SHORT.map(d => (
          <div key={d} className="text-center" style={{ fontSize: '11px', color: 'var(--muted-fg)', fontWeight: 600, padding: '4px 0' }}>
            {d}
          </div>
        ))}
      </div>

      {/* Day cells */}
      <div className="grid grid-cols-7 gap-1">
        {cells.map((date, i) => {
          if (!date) return <div key={`e-${i}`} />;
          const dateStr = toDateStr(date);
          const isPast = dateStr < todayStr;
          const isDisabledDay = disabledDays.has(JS_DAY_TO_BACKEND[date.getDay()]);
          const disabled = isPast || isDisabledDay;
          const isSelected = dateStr === selectedDate;
          const isToday = isSameDay(date, today);

          return (
            <button
              key={dateStr}
              onClick={() => !disabled && onSelectDate(dateStr)}
              disabled={disabled}
              className="relative flex items-center justify-center rounded-lg transition-colors"
              style={{
                width: '100%',
                aspectRatio: '1',
                fontSize: '13px',
                fontWeight: isSelected ? 600 : 400,
                backgroundColor: isSelected ? 'var(--terracotta)' : 'transparent',
                color: isSelected ? 'white' : disabled ? 'var(--border-color)' : 'var(--dark-teal)',
                cursor: disabled ? 'not-allowed' : 'pointer',
                border: isToday && !isSelected ? '2px solid var(--terracotta)' : 'none',
              }}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Component ──────────────────────────────────────────────────────────

export function AppointmentWizard() {
  const navigate = useNavigate();
  const businessId = useBusinessId();
  const queryClient = useQueryClient();
  const currency = useBusinessCurrency();
  const formatPrice = (price: number) => formatCurrency(price, currency);

  const [step, setStep] = useState(1);
  const [selectedServiceIds, setSelectedServiceIds] = useState<number[]>([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  // Held as the full object, not just an id: a customer created mid-flow (or one
  // scrolled past on another page of results) is not in the current search response,
  // and deriving the selection from that list would blank out the confirm step.
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const selectedCustomerId = selectedCustomer?.id ?? null;
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [debouncedCustomerTerm, setDebouncedCustomerTerm] = useState('');
  const [customerPage, setCustomerPage] = useState(0);
  const [showCreateCustomer, setShowCreateCustomer] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [stepError, setStepError] = useState('');

  const TOTAL_STEPS = 4;
  const stepLabels = ['Servicios', 'Fecha y hora', 'Cliente', 'Confirmar'];

  const nextStep = () => {
    setStepError('');
    if (step === 1 && selectedServiceIds.length === 0) {
      setStepError('Selecciona al menos un servicio');
      return;
    }
    if (step === 2 && (!selectedDate || !selectedTime)) {
      setStepError('Selecciona una fecha y un horario');
      return;
    }
    if (step === 3 && selectedCustomerId == null) {
      setStepError('Selecciona un cliente');
      return;
    }
    setStep(s => s + 1);
  };
  const prevStep = () => { setStepError(''); setStep(s => s - 1); };

  // ── Fetch current user (staff member) ────────────────────────────────
  const { data: currentStaff } = useQuery({
    queryKey: ['user-me'],
    queryFn: () => userApi.getMe(),
    staleTime: 5 * 60_000,
  });

  // ── Work hours (to disable non-working days on calendar) ─────────────
  const { data: workHours = [] } = useQuery({
    queryKey: ['my-work-hours'],
    queryFn: workHoursApi.getMyWorkHours,
    staleTime: 60_000,
  });

  const disabledDays = useMemo(() => {
    const disabled = new Set<string>();
    for (const wh of workHours) {
      if (!wh.enabled) disabled.add(wh.dayOfWeek);
    }
    return disabled;
  }, [workHours]);

  // ── Step 1: Services assigned to the logged-in staff member ──────────
  const { data: categories = [], isLoading: loadingCategories } = useQuery({
    queryKey: ['my-services', businessId, currentStaff?.id],
    queryFn: () => catalogApi.getMyServices(businessId, currentStaff!.id),
    enabled: !!businessId && !!currentStaff?.id,
    staleTime: 60_000,
  });

  const servicesById = useMemo(() => {
    const map = new Map<number, CatalogService>();
    categories.forEach((cat: CatalogCategory) =>
      cat.services.filter(s => s.active).forEach(s => map.set(s.id, s)),
    );
    return map;
  }, [categories]);

  const selectedServices = useMemo(
    () => selectedServiceIds.map(id => servicesById.get(id)).filter(Boolean) as CatalogService[],
    [selectedServiceIds, servicesById],
  );

  const totalDuration = useMemo(
    () => selectedServices.reduce((sum, s) => sum + s.duration, 0),
    [selectedServices],
  );

  const totalPrice = useMemo(
    () => selectedServices.reduce((sum, s) => sum + s.price, 0),
    [selectedServices],
  );

  const toggleService = useCallback((serviceId: number) => {
    setStepError('');
    setSelectedServiceIds(prev =>
      prev.includes(serviceId) ? prev.filter(id => id !== serviceId) : [...prev, serviceId],
    );
  }, []);

  // ── Step 2: Availability time slots ──────────────────────────────────
  const { data: timeSlots = [], isLoading: loadingSlots } = useQuery({
    queryKey: ['availability', businessId, currentStaff?.id, selectedDate, totalDuration],
    queryFn: () =>
      teamApi.getAvailability(businessId, currentStaff!.id, selectedDate, totalDuration),
    enabled: !!businessId && !!currentStaff?.id && !!selectedDate && totalDuration > 0,
    staleTime: 30_000,
  });

  const availableSlots = useMemo(
    () => timeSlots.filter((s: TimeSlot) => s.available),
    [timeSlots],
  );

  // ── Step 3: Customer search ──────────────────────────────────────────
  // Debounced so typing a name is one request, not one per keystroke.
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedCustomerTerm(customerSearchTerm), 300);
    return () => clearTimeout(handle);
  }, [customerSearchTerm]);

  // Paged server-side: a salon with thousands of clients would otherwise ship its whole
  // customer book to the tablet on every visit to this step.
  const { data: customerPageData, isLoading: loadingCustomers } = useQuery({
    queryKey: ['customers-search', businessId, debouncedCustomerTerm, customerPage],
    queryFn: () =>
      customersApi.searchPaged(businessId, debouncedCustomerTerm, customerPage, CUSTOMER_PAGE_SIZE),
    enabled: !!businessId && step === 3,
    staleTime: 15_000,
    placeholderData: previous => previous,
  });

  const visibleCustomers = customerPageData?.content ?? [];
  const totalCustomers = customerPageData?.totalElements ?? 0;
  const customerPageCount = Math.max(1, customerPageData?.totalPages ?? 1);

  useEffect(() => {
    setCustomerPage(0);
  }, [debouncedCustomerTerm]);

  // ── Step 4: Create appointment mutation ──────────────────────────────
  const createMutation = useMutation({
    mutationFn: () =>
      appointmentsApi.create(businessId, {
        customerId: selectedCustomerId!,
        userId: currentStaff!.id,
        appointmentDate: selectedDate,
        startTime: selectedTime.slice(0, 5),
        services: selectedServiceIds.map(id => ({ serviceId: id })),
      }),
    onSuccess: () => {
      toast.success('Cita creada exitosamente');
      navigate('/');
    },
    onError: (err: unknown) => {
      setCreateError(getApiErrorMessage(err, 'Error al crear la cita. Intenta de nuevo.'));
    },
  });

  const handleConfirm = () => {
    setCreateError(null);
    createMutation.mutate();
  };

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--cream)' }}>
      {/* Progress Bar */}
      <div className="flex-shrink-0" style={{ padding: '16px 14px' }}>
        <div
          className="rounded-xl"
          style={{
            backgroundColor: 'var(--off-white)',
            border: '1px solid var(--border-color)',
            boxShadow: 'var(--shadow-card)',
            padding: '14px 18px',
          }}
        >
          <div className="flex items-center gap-3">
            <span style={{ fontSize: '13px', color: 'var(--muted-fg)' }}>
              Paso {step}/{TOTAL_STEPS} — {stepLabels[step - 1]}
            </span>
            <div className="flex-1 flex gap-2">
              {Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1).map(s => (
                <div
                  key={s}
                  className="flex-1 rounded-full transition-colors"
                  style={{
                    height: '6px',
                    backgroundColor: s <= step ? 'var(--terracotta)' : 'var(--border-color)',
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Step Content */}
      <div className="flex-1 overflow-auto px-4 md:px-6 pb-24">
        {stepError && (
          <div className="max-w-3xl mx-auto mb-3">
            <div className="flex items-center gap-2 rounded-lg" style={{ padding: '10px 14px', backgroundColor: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.15)' }}>
              <AlertCircle size={16} style={{ color: '#dc2626', flexShrink: 0 }} />
              <span style={{ fontSize: '13px', color: '#dc2626' }}>{stepError}</span>
            </div>
          </div>
        )}

        {/* ─── Step 1: Services (2 columns) ────────────────────────── */}
        {step === 1 && (
          <div className="max-w-3xl mx-auto">
            <div style={{ fontFamily: 'var(--font-heading)', fontSize: '20px', color: 'var(--dark-teal)', marginBottom: '16px' }}>
              Seleccionar Servicios
            </div>

            {loadingCategories ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 size={28} className="animate-spin" style={{ color: 'var(--terracotta)' }} />
              </div>
            ) : categories.length === 0 ? (
              <div style={{ fontSize: '14px', color: 'var(--muted-fg)', textAlign: 'center', padding: '32px 0' }}>
                No hay servicios disponibles
              </div>
            ) : (
              <div className="space-y-5">
                {categories.map((category: CatalogCategory) => {
                  const activeServices = category.services.filter(s => s.active);
                  if (activeServices.length === 0) return null;
                  return (
                    <div key={category.id}>
                      <div className="section-label" style={{ marginBottom: '8px' }}>
                        {category.name}
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {activeServices.map((service: CatalogService) => {
                          const isSelected = selectedServiceIds.includes(service.id);
                          return (
                            <button
                              key={service.id}
                              onClick={() => toggleService(service.id)}
                              className="text-left rounded-xl transition-colors flex items-start gap-2.5"
                              style={{
                                padding: '12px 14px',
                                backgroundColor: isSelected ? 'var(--terracotta)' : 'var(--off-white)',
                                color: isSelected ? 'white' : 'var(--dark-teal)',
                                border: isSelected ? 'none' : '1px solid var(--border-color)',
                                fontSize: '13px',
                              }}
                            >
                              <div
                                className="flex-shrink-0 rounded transition-colors flex items-center justify-center mt-0.5"
                                style={{
                                  width: '18px',
                                  height: '18px',
                                  border: isSelected ? 'none' : '2px solid var(--border-color)',
                                  backgroundColor: isSelected ? 'rgba(255, 255, 255, 0.3)' : 'transparent',
                                  borderRadius: '4px',
                                }}
                              >
                                {isSelected && <Check size={12} strokeWidth={3} />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="truncate" style={{ fontWeight: 500 }}>{service.name}</div>
                                <div style={{ fontSize: '11px', opacity: isSelected ? 0.8 : 0.5, marginTop: '2px' }}>
                                  {service.duration} min · {formatPrice(service.price)}
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {selectedServiceIds.length > 0 && (
              <div className="mt-6">
                <div style={{ fontSize: '13px', color: 'var(--muted-fg)', marginBottom: '8px', textAlign: 'center' }}>
                  {selectedServiceIds.length} servicio{selectedServiceIds.length !== 1 ? 's' : ''} · {totalDuration} min · {formatPrice(totalPrice)}
                </div>
                <button
                  onClick={nextStep}
                  className="w-full rounded-lg transition-colors"
                  style={{ height: '44px', backgroundColor: 'var(--terracotta)', color: 'white', fontSize: '14px', fontWeight: 500 }}
                >
                  Continuar
                </button>
              </div>
            )}
          </div>
        )}

        {/* ─── Step 2: Date + Time (merged) ────────────────────────── */}
        {step === 2 && (
          <div className="max-w-3xl mx-auto">
            <div style={{ fontFamily: 'var(--font-heading)', fontSize: '20px', color: 'var(--dark-teal)', marginBottom: '16px' }}>
              Seleccionar Fecha y Hora
            </div>

            <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-4">
              {/* Calendar */}
              <div
                className="rounded-xl"
                style={{ backgroundColor: 'var(--off-white)', border: '1px solid var(--border-color)', padding: '16px' }}
              >
                <MiniCalendar
                  selectedDate={selectedDate}
                  onSelectDate={(dateStr) => {
                    setSelectedDate(dateStr);
                    setSelectedTime('');
                    setStepError('');
                  }}
                  disabledDays={disabledDays}
                />
              </div>

              {/* Time slots */}
              <div
                className="rounded-xl"
                style={{ backgroundColor: 'var(--off-white)', border: '1px solid var(--border-color)', padding: '16px' }}
              >
                {!selectedDate ? (
                  <div className="flex items-center justify-center h-full" style={{ minHeight: '200px' }}>
                    <span style={{ fontSize: '14px', color: 'var(--muted-fg)' }}>
                      Selecciona una fecha en el calendario
                    </span>
                  </div>
                ) : (
                  <>
                    <div style={{ fontSize: '13px', color: 'var(--muted-fg)', marginBottom: '12px', fontWeight: 500 }}>
                      Horarios disponibles
                    </div>
                    {loadingSlots ? (
                      <div className="flex items-center justify-center" style={{ minHeight: '160px' }}>
                        <Loader2 size={24} className="animate-spin" style={{ color: 'var(--terracotta)' }} />
                      </div>
                    ) : availableSlots.length === 0 ? (
                      <div className="flex items-center justify-center" style={{ minHeight: '160px' }}>
                        <span style={{ fontSize: '13px', color: 'var(--muted-fg)' }}>
                          No hay horarios disponibles para esta fecha
                        </span>
                      </div>
                    ) : (
                      <div className="grid grid-cols-3 gap-2" style={{ maxHeight: '340px', overflowY: 'auto' }}>
                        {availableSlots.map((slot: TimeSlot) => {
                          const isSelected = selectedTime === slot.time;
                          return (
                            <button
                              key={slot.time}
                              onClick={() => {
                                setStepError('');
                                setSelectedTime(slot.time);
                              }}
                              className="rounded-lg transition-colors"
                              style={{
                                padding: '10px 8px',
                                fontSize: '13px',
                                fontWeight: isSelected ? 600 : 400,
                                backgroundColor: isSelected ? 'var(--terracotta)' : 'transparent',
                                color: isSelected ? 'white' : 'var(--dark-teal)',
                                border: isSelected ? 'none' : '1px solid var(--border-color)',
                              }}
                            >
                              {formatTime(slot.time)}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Continue button */}
            {selectedDate && selectedTime && (
              <div className="mt-5">
                <button
                  onClick={nextStep}
                  className="w-full rounded-lg transition-colors"
                  style={{ height: '44px', backgroundColor: 'var(--terracotta)', color: 'white', fontSize: '14px', fontWeight: 500 }}
                >
                  Continuar
                </button>
              </div>
            )}
          </div>
        )}

        {/* ─── Step 3: Customer ─────────────────────────────────────── */}
        {step === 3 && (
          <div className="max-w-3xl mx-auto">
            <div style={{ fontFamily: 'var(--font-heading)', fontSize: '20px', color: 'var(--dark-teal)', marginBottom: '16px' }}>
              Seleccionar Cliente
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-4">
              <div className="relative flex-1">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-fg)' }} />
                <input
                  type="text"
                  placeholder="Buscar clientes..."
                  value={customerSearchTerm}
                  onChange={e => setCustomerSearchTerm(e.target.value)}
                  className="w-full rounded-xl"
                  style={{
                    height: '44px', padding: '0 16px 0 36px',
                    border: '1px solid var(--border-color)', backgroundColor: 'var(--off-white)',
                    fontSize: '14px', color: 'var(--dark-teal)',
                  }}
                />
              </div>
              <button
                type="button"
                onClick={() => setShowCreateCustomer(true)}
                className="rounded-xl flex items-center justify-center gap-2 flex-shrink-0"
                style={{
                  height: '44px', padding: '0 16px',
                  border: '1px solid var(--border-color)', backgroundColor: 'var(--off-white)',
                  fontSize: '14px', color: 'var(--dark-teal)',
                }}
              >
                <Plus size={16} />
                Nuevo cliente
              </button>
            </div>

            {loadingCustomers ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 size={24} className="animate-spin" style={{ color: 'var(--terracotta)' }} />
              </div>
            ) : totalCustomers === 0 ? (
              <div
                className="rounded-2xl flex flex-col items-center text-center"
                style={{
                  padding: '32px 24px',
                  backgroundColor: 'var(--off-white)',
                  border: '1px solid var(--border-color)',
                }}
              >
                <div
                  className="rounded-full flex items-center justify-center mb-3"
                  style={{ width: '48px', height: '48px', backgroundColor: 'var(--muted-bg)' }}
                >
                  <UserPlus size={22} style={{ color: 'var(--muted-fg)' }} />
                </div>
                <div style={{ fontFamily: 'var(--font-heading)', fontSize: '16px', color: 'var(--dark-teal)', marginBottom: '6px' }}>
                  {debouncedCustomerTerm ? 'No se encontraron clientes' : 'Aún no hay clientes'}
                </div>
                <p style={{ fontSize: '13px', color: 'var(--muted-fg)', lineHeight: 1.5, maxWidth: '320px', marginBottom: '16px' }}>
                  {debouncedCustomerTerm
                    ? `Ningún cliente coincide con "${debouncedCustomerTerm}". Agrégalo para continuar con la cita.`
                    : 'Necesitas un cliente para crear una cita. Agrega el primero para continuar.'}
                </p>
                <button
                  type="button"
                  onClick={() => setShowCreateCustomer(true)}
                  className="rounded-xl flex items-center justify-center gap-2"
                  style={{
                    height: '44px', padding: '0 20px',
                    backgroundColor: 'var(--terracotta)', color: 'white',
                    fontSize: '14px', fontWeight: 500, border: 'none', cursor: 'pointer',
                  }}
                >
                  <Plus size={16} />
                  Agregar cliente
                </button>
              </div>
            ) : (
              <div className="space-y-2 mb-4">
                {visibleCustomers.map((customer: Customer) => {
                  const isSelected = selectedCustomerId === customer.id;
                  return (
                    <button
                      key={customer.id}
                      onClick={() => {
                        setStepError('');
                        setSelectedCustomer(customer);
                        setStep(4);
                      }}
                      className="w-full text-left rounded-xl transition-colors flex items-center gap-3"
                      style={{
                        padding: '12px 16px',
                        backgroundColor: isSelected ? 'var(--terracotta)' : 'var(--off-white)',
                        color: isSelected ? 'white' : 'var(--dark-teal)',
                        border: isSelected ? 'none' : '1px solid var(--border-color)',
                      }}
                    >
                      <div
                        className="rounded-full flex items-center justify-center flex-shrink-0"
                        style={{
                          width: '36px', height: '36px',
                          backgroundColor: isSelected ? 'rgba(255,255,255,0.2)' : 'var(--peach)',
                          fontSize: '12px', fontWeight: 600,
                        }}
                      >
                        {getInitials(customer.firstName, customer.lastName)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate" style={{ fontSize: '14px', fontFamily: 'var(--font-heading)' }}>
                          {customer.firstName} {customer.lastName}
                        </div>
                        {customer.phone && (
                          <div className="truncate" style={{ fontSize: '12px', opacity: isSelected ? 0.8 : 0.5 }}>
                            {customer.phone}
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}

                {totalCustomers > CUSTOMER_PAGE_SIZE && (
                  <div className="flex items-center justify-between pt-2">
                    <span style={{ fontSize: '12px', color: 'var(--muted-fg)' }}>
                      {customerPage * CUSTOMER_PAGE_SIZE + 1}–
                      {Math.min((customerPage + 1) * CUSTOMER_PAGE_SIZE, totalCustomers)} de {totalCustomers}
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setCustomerPage(p => Math.max(0, p - 1))}
                        disabled={customerPage === 0}
                        aria-label="Página anterior"
                        className="rounded-lg flex items-center justify-center disabled:opacity-30"
                        style={{
                          width: '36px', height: '36px',
                          border: '1px solid var(--border-color)', backgroundColor: 'var(--off-white)',
                          color: 'var(--dark-teal)',
                        }}
                      >
                        <ChevronLeft size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setCustomerPage(p => Math.min(customerPageCount - 1, p + 1))}
                        disabled={customerPage >= customerPageCount - 1}
                        aria-label="Página siguiente"
                        className="rounded-lg flex items-center justify-center disabled:opacity-30"
                        style={{
                          width: '36px', height: '36px',
                          border: '1px solid var(--border-color)', backgroundColor: 'var(--off-white)',
                          color: 'var(--dark-teal)',
                        }}
                      >
                        <ChevronRight size={16} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ─── Step 4: Confirm ──────────────────────────────────────── */}
        {step === 4 && (
          <div className="max-w-2xl mx-auto">
            <div style={{ fontFamily: 'var(--font-heading)', fontSize: '20px', color: 'var(--dark-teal)', marginBottom: '16px' }}>
              Confirmar Cita
            </div>
            <div
              className="rounded-xl space-y-3 mb-6"
              style={{
                backgroundColor: 'var(--off-white)', border: '1px solid var(--border-color)',
                boxShadow: 'var(--shadow-card)', padding: '18px 20px',
              }}
            >
              <div className="flex justify-between" style={{ fontSize: '14px' }}>
                <span style={{ color: 'var(--muted-fg)' }}>Cliente</span>
                <span style={{ color: 'var(--dark-teal)' }}>
                  {selectedCustomer ? `${selectedCustomer.firstName} ${selectedCustomer.lastName}` : '—'}
                </span>
              </div>
              <div className="flex justify-between items-start gap-3 min-w-0" style={{ fontSize: '14px' }}>
                <span className="flex-shrink-0" style={{ color: 'var(--muted-fg)' }}>Servicio{selectedServices.length !== 1 ? 's' : ''}</span>
                <span className="min-w-0 break-words" style={{ color: 'var(--dark-teal)', textAlign: 'right' }}>
                  {selectedServices.map(s => s.name).join(', ')}
                </span>
              </div>
              <div className="flex justify-between" style={{ fontSize: '14px' }}>
                <span style={{ color: 'var(--muted-fg)' }}>Duración</span>
                <span style={{ color: 'var(--dark-teal)' }}>{totalDuration} min</span>
              </div>
              <div className="flex justify-between" style={{ fontSize: '14px' }}>
                <span style={{ color: 'var(--muted-fg)' }}>Precio</span>
                <span style={{ color: 'var(--dark-teal)' }}>{formatPrice(totalPrice)}</span>
              </div>
              <div className="flex justify-between" style={{ fontSize: '14px' }}>
                <span style={{ color: 'var(--muted-fg)' }}>Fecha</span>
                <span style={{ color: 'var(--dark-teal)' }}>{selectedDate}</span>
              </div>
              <div className="flex justify-between" style={{ fontSize: '14px' }}>
                <span style={{ color: 'var(--muted-fg)' }}>Hora</span>
                <span style={{ color: 'var(--dark-teal)' }}>{formatTime(selectedTime)}</span>
              </div>
              <div className="flex justify-between" style={{ fontSize: '14px' }}>
                <span style={{ color: 'var(--muted-fg)' }}>Profesional</span>
                <span style={{ color: 'var(--dark-teal)' }}>
                  {currentStaff ? `${currentStaff.firstName} ${currentStaff.lastName}` : '—'}
                </span>
              </div>
            </div>

            {createError && (
              <div className="flex items-center gap-2 rounded-lg mb-4" style={{ padding: '12px 16px', backgroundColor: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626', fontSize: '13px' }}>
                <AlertCircle size={16} />
                <span>{createError}</span>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={handleConfirm}
                disabled={createMutation.isPending}
                className="flex-1 flex items-center justify-center gap-2 rounded-lg transition-colors"
                style={{
                  height: '44px', backgroundColor: 'var(--terracotta)', color: 'white',
                  fontSize: '14px', fontWeight: 500, opacity: createMutation.isPending ? 0.7 : 1,
                }}
              >
                {createMutation.isPending ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                {createMutation.isPending ? 'Creando...' : 'Crear Cita'}
              </button>
              <button
                onClick={() => navigate(-1)}
                disabled={createMutation.isPending}
                className="rounded-lg transition-colors hover:bg-[#FAF9F7]"
                style={{ height: '44px', padding: '0 20px', border: '1px solid var(--border-color)', color: 'var(--dark-teal)', fontSize: '14px' }}
              >
                Cancelar
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Back button */}
      {step > 1 && (step < TOTAL_STEPS || (step === TOTAL_STEPS && !createMutation.isPending)) && (
        <div
          className="flex-shrink-0"
          style={{ padding: '12px 14px', borderTop: '1px solid var(--border-color)', backgroundColor: 'var(--off-white)' }}
        >
          <button
            onClick={prevStep}
            className="flex items-center gap-1 transition-colors"
            style={{ fontSize: '14px', color: 'var(--dark-teal)' }}
          >
            <ChevronLeft size={16} />
            Atrás
          </button>
        </div>
      )}

      <CreateCustomerModal
        open={showCreateCustomer}
        onClose={() => setShowCreateCustomer(false)}
        businessId={businessId}
        initialFirstName={customerSearchTerm}
        onCreated={customer => {
          setShowCreateCustomer(false);
          setStepError('');
          // Refresh the picker for a later visit to step 3, but don't wait on it —
          // the new customer is selected directly so the flow moves on immediately.
          queryClient.invalidateQueries({ queryKey: ['customers-search', businessId] });
          setSelectedCustomer(customer);
          setStep(4);
        }}
      />
    </div>
  );
}
